import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SkeletonLoader from '../../../components/SkeletonLoader';
import { useToast } from '../../../contexts/ToastContext';
import { fetchBlogs } from '../../../api/blogApi';

function BlogSection() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchBlogPostsData = async () => {
      try {
        setLoading(true);
        const data = await fetchBlogs({ limit: 3, paginate: false });

        if (data.success) {
          const formattedPosts = data.data.map(blog => ({
            id: blog.id,
            image: blog.image,
            title: blog.title,
            date: new Date(blog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
            description: blog.excerpt,
            slug: blog.slug,
          }));
          setBlogPosts(formattedPosts);
        } else {
          addToast('error', data.message || 'Failed to fetch blog posts.');
        }
      } catch (e) {
        console.error("Error fetching blog posts:", e);
        addToast('error', 'Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPostsData();
  }, [addToast]);

  // If no blog posts and not loading, return null to hide the section
  if (!loading && blogPosts.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-bg-dark text-text-gray border-y border-border-subtle">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-center text-text-white relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px] mb-12">Latest Blog Posts</h2>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <SkeletonLoader type="blog-card" count={3} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <div key={post.id} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-border-subtle">
                  <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
                    <p className="text-text-gray text-sm mb-4">{post.date}</p>
                    <p className="text-text-gray mb-4">{post.description}</p>
                    <Link
                      to={`/blogs/${post.slug}`}
                      className="inline-block text-accent-red hover:underline mt-2"
                    >
                      Read More →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/blogs"
                className="inline-block bg-accent-red text-white px-8 py-3 rounded-full text-lg font-semibold transition-all duration-300 hover:bg-red-700 hover:-translate-y-1"
              >
                View All Blogs
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default BlogSection;